import { format, parseISO } from 'date-fns';
import { Fragment, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { RewardDistributionResult } from '../../types/reward-distribution.types';

import { Pagination } from 'components/pagination';

import { useRewardDistributionHistory } from '../../hooks/useRewardDistributionHistory';

import { TableHeaderRow } from '../EventArenaSection/TableHeaderRow';
import { AUTO_DISTRIBUTION_TYPES, COLUMNS } from './constants';

type ColorVariant = 'dark' | 'light';

const ColumnSeparator = () => <div className="tw-h-[8px] tw-w-[0.5px] tw-shrink-0 tw-bg-white lg:tw-hidden" />;

const renderCellsWithSeparators = (cells: ReactNode[]) =>
  cells.map((cell, i) => (
    <Fragment key={i}>
      {i > 0 && <ColumnSeparator />}
      {cell}
    </Fragment>
  ));

const TableRow = ({ children, variant }: { children: ReactNode; variant: ColorVariant }) => (
  <div
    className={twMerge(
      'tw-flex tw-h-[45px] tw-flex-row tw-items-center tw-gap-[8px] tw-px-[16px] lg:tw-h-[36px] lg:tw-gap-[16px]',
      variant === 'dark' && 'tw-bg-rewardDistributionHistoryRowGradient',
      variant === 'light' && 'tw-bg-leaderboardRowGradient '
    )}
  >
    {children}
  </div>
);

const renderDistributionStatus = (item: RewardDistributionResult) => {
  if (item.distribution_status === 'Distributed') {
    if (AUTO_DISTRIBUTION_TYPES.includes(item.reward_distribution_type)) {
      return 'Distributed';
    }
    // Manual distribution
    if (item.distribution_link) {
      return (
        <>
          Completed{' '}
          <a
            href={item.distribution_link}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-white tw-underline hover:tw-opacity-80"
          >
            (Details)
          </a>
        </>
      );
    }
    return 'Completed';
  }

  return item.distribution_status;
};

type RewardDistributionHistorySectionProps = {
  eventId?: string;
  variant?: ColorVariant;
};

const RewardDistributionHistorySection = ({ eventId, variant = 'dark' }: RewardDistributionHistorySectionProps) => {
  const {
    displayItems,
    isEmpty,
    showPagination,
    currentPage,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
  } = useRewardDistributionHistory(eventId);

  const isDarkVariant = variant === 'dark';

  const cellClassName =
    'tw-flex-1 tw-text-[10px] lg:tw-leading-[24px] tw-text-white lg:tw-text-[15px] tw-line-clamp-2 lg:tw-line-clamp-1';

  return (
    <div
      className={twMerge('tw-mx-auto tw-my-[40px] tw-max-w-[1280px] lg:tw-my-[100px]', isDarkVariant && 'tw-font-jost')}
    >
      <h2 className="tw-px-[20px] tw-text-[32px] tw-font-bold tw-text-white lg:tw-px-0 lg:tw-text-[40px]">
        Reward Distribution History
      </h2>

      <div className="tw-mt-[40px]">
        <div className="tw-mb-[8px] tw-flex tw-flex-row tw-items-end tw-items-center tw-gap-[16px] tw-px-[16px] tw-text-white lg:tw-mb-[30px]">
          {COLUMNS.map((column) => (
            <TableHeaderRow
              key={column.key}
              title={column.header}
              variant={isDarkVariant ? 'rewardDistributionHistory' : 'rewardDistributionHistoryLight'}
            />
          ))}
        </div>

        <div className="tw-flex tw-flex-col tw-gap-[6px] lg:tw-gap-[16px]">
          {isEmpty ? (
            <TableRow variant={variant}>
              {renderCellsWithSeparators(
                COLUMNS.map((column) => (
                  <div key={column.key} className={cellClassName}>
                    -
                  </div>
                ))
              )}
            </TableRow>
          ) : (
            displayItems.map((item, index) => (
              <div key={`${item.created_at}-${index}`}>
                <TableRow variant={variant}>
                  {renderCellsWithSeparators([
                    <div className={cellClassName}>{item.name}</div>,
                    <div className={cellClassName}>{format(parseISO(item.created_at), 'yyyy-MM-dd HH:mm:ss')}</div>,
                    <div className={cellClassName}>{item.prize.name}</div>,
                    <div className={cellClassName}>{renderDistributionStatus(item)}</div>,
                  ])}
                </TableRow>
              </div>
            ))
          )}
        </div>

        {showPagination && totalPages > 1 && (
          <>
            <div className="tw-mb-[20px] lg:tw-mb-[38px]" />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              goToPage={goToPage}
              goToNextPage={goToNextPage}
              goToPreviousPage={goToPreviousPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RewardDistributionHistorySection;
